import { getCustomOptionText, isCustomOptionValue } from '@shared/helpers/custom-option-value.helper';
import {
  CONFIGURATOR_ADDRESS_FIELDS,
  EConfiguratorQuestionType,
  IConfiguratorFormatContext,
  IConfiguratorInvitee,
  IConfiguratorInvoiceAddress,
  IConfiguratorQuestion
} from '../models/configurator.model';
import { resolveText } from './configurator-rules.helper';
import {
  formatAddressLine,
  getEffectiveAddress,
  getFilledInvitees,
  getInviteeName,
  getInvoiceName
} from './configurator-contact.helper';

const T = 'ProjectConfiguratorV2.Summary';

/** Longer answers are cut in the summary, as in the prototype. */
export const SUMMARY_VALUE_MAX_LENGTH = 70;

export const truncateSummaryValue = (text: string, maxLength = SUMMARY_VALUE_MAX_LENGTH): string =>
  text.length > maxLength ? `${text.slice(0, maxLength - 2)} …` : text;

/** Question label for the summary: `summaryLabel`, otherwise the label without a trailing "?". */
export const getSummaryLabel = (
  question: IConfiguratorQuestion,
  context: IConfiguratorFormatContext
): string => {
  if (question.summaryLabel) return context.translate(question.summaryLabel);
  const label = resolveText(question.label, context.answers);
  return label ? context.translate(label).replace(/\s*\?\s*$/, '') : '';
};

export const formatNumber = (value: number, decimalSymbol: string): string =>
  value.toString().replace('.', decimalSymbol);

/** `YYYY-MM-DD` → `DD.MM.YYYY`, as dates are shown in the app. */
export const formatDate = (value: string): string => {
  const [year, month, day] = value.split('-');
  return day && month && year ? `${day}.${month}.${year}` : value;
};

const formatOption = (
  question: IConfiguratorQuestion,
  value: string,
  context: IConfiguratorFormatContext
) => {
  if (isCustomOptionValue(value)) return getCustomOptionText(value);
  const option = question.options?.find((item) => item.value === value);
  return option ? context.translate(option.label) : value;
};

const formatCounters = (
  question: IConfiguratorQuestion,
  value: Record<string, number>,
  context: IConfiguratorFormatContext
): string => {
  const names = question.summaryItemNames?.(context.answers) || {};
  const keys = [
    ...(question.items || []).map((item) => item.key),
    ...Object.keys(value).filter((key) => isCustomOptionValue(key))
  ];

  return keys
    .filter((key) => value[key] > 0)
    .map((key) => {
      const count = value[key];
      if (isCustomOptionValue(key)) return getCustomOptionText(key);

      const item = question.items?.find((entry) => entry.key === key);
      const prefix = count > 1 || question.summaryAlwaysCount ? `${count}× ` : '';
      const itemNames = names[key]?.length ? ` (${names[key].join(', ')})` : '';
      return `${prefix}${context.translate(item?.label || key)}${itemNames}`;
    })
    .join(', ');
};

const formatAddress = (question: IConfiguratorQuestion, context: IConfiguratorFormatContext): string => {
  const fields = question.addressFields || CONFIGURATOR_ADDRESS_FIELDS;
  if (fields.unknown && context.answers[fields.unknown]) return context.translate(`${T}.addressUnknown`);
  return formatAddressLine(getEffectiveAddress(fields, context.answers));
};

/** Answer as text for the summary, e.g. option labels joined with ", " or "2× Bad, Küche". */
export const formatAnswer = (
  question: IConfiguratorQuestion,
  value: unknown,
  context: IConfiguratorFormatContext
): string => {
  if (question.summaryValue) return question.summaryValue(value, context);

  switch (question.type) {
    case EConfiguratorQuestionType.CHIPS:
    case EConfiguratorQuestionType.TILES:
    case EConfiguratorQuestionType.TRIANGLE:
      return (Array.isArray(value) ? value : [value])
        .map((item) => formatOption(question, String(item), context))
        .join(', ');
    case EConfiguratorQuestionType.NUMBER: {
      const number = formatNumber(Number(value), context.decimalSymbol);
      return question.suffix ? `${number} ${context.translate(question.suffix)}` : number;
    }
    case EConfiguratorQuestionType.COUNTERS:
      return formatCounters(question, (value || {}) as Record<string, number>, context);
    case EConfiguratorQuestionType.ADDRESS:
      return formatAddress(question, context);
    case EConfiguratorQuestionType.DATE:
      return formatDate(String(value));
    case EConfiguratorQuestionType.LINKS: {
      const links = (value || []) as string[];
      return links.length === 1 ? links[0] : context.translate(`${T}.links`, { count: links.length });
    }
    case EConfiguratorQuestionType.INVOICE_ADDRESS:
      return getInvoiceName(value as IConfiguratorInvoiceAddress);
    case EConfiguratorQuestionType.INVITEES:
      return getFilledInvitees(value as IConfiguratorInvitee[])
        .map(getInviteeName)
        .join(', ');
    case EConfiguratorQuestionType.CHECKBOX:
      return question.text ? context.translate(question.text) : '';
    default:
      return String(value ?? '');
  }
};
